import { EmptyState } from '../../components/EmptyState';
import { Skeleton } from '../../components/Skeleton';
import { useAccountingView } from '../../hooks/useAccountingView';

import styles from './Accounting.module.css';
import { ACCOUNTING_EMPTY } from './empty';
import { MoneyCell, commitsSub } from './MoneyCell';
import {
  TREATMENT_NODE_LABELS,
  fmtPct,
  rollupTreatmentByFunction,
  type TreatmentRow,
} from './rollups';

// Treatment is always the columns; business functions are the rows.
const COLS: Array<{ key: TreatmentRow }> = [
  { key: 'opex_rd' },
  { key: 'opex_other' },
  { key: 'capex' },
];

/**
 * Report 2: Business Function × Accounting Treatment. Same shape as
 * Report 1, plus a "Flagged for review" column that overlaps the treatment
 * totals (those commits are also counted in their treatment column) — the
 * column transpose of the old flagged sub-row.
 */
export function TreatmentByFunctionView() {
  const { data, isPending, error } = useAccountingView();
  if (isPending) return <Skeleton title='Treatment × Function' rows={5} />;
  if (error) return <div className={styles.errorBox}>Failed to load accounting view: {error.message}</div>;
  if (!data) return null;
  if (data.commits.length === 0) return <EmptyState {...ACCOUNTING_EMPTY} />;

  const r = rollupTreatmentByFunction(data);
  const bfs = data.business_functions;

  return (
    <table className={styles.matrix}>
      <thead>
        <tr>
          <th>Business Function</th>
          {COLS.map(({ key }) => (
            <th key={key}>{TREATMENT_NODE_LABELS[key]}</th>
          ))}
          <th className={styles.colFlagged}>Flagged for review</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        {bfs.map((bf) => {
          const rowTotal = r.functionTotals[bf.id];
          const flaggedCell = r.flagged.cells[`flagged|${bf.id}`];
          return (
            <tr key={bf.id}>
              <td>{bf.display}</td>
              {COLS.map(({ key }) => {
                const cell = r.cells[`${key}|${bf.id}`];
                return (
                  <td key={key}>
                    <MoneyCell cost={cell?.cost_usd ?? 0} sub={commitsSub(cell?.count ?? 0)} />
                  </td>
                );
              })}
              <td className={styles.colFlagged}>
                <MoneyCell
                  cost={flaggedCell?.cost_usd ?? 0}
                  sub={commitsSub(flaggedCell?.count ?? 0)}
                />
              </td>
              <td>
                <MoneyCell
                  cost={rowTotal?.cost_usd ?? 0}
                  sub={`${fmtPct(rowTotal?.cost_usd ?? 0, r.grandTotal.cost_usd)} of total`}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <td>Total</td>
          {COLS.map(({ key }) => {
            const colTotal = r.treatmentTotals[key];
            return (
              <td key={key}>
                <MoneyCell cost={colTotal.cost_usd} sub={commitsSub(colTotal.count)} />
              </td>
            );
          })}
          <td className={styles.colFlagged}>
            <MoneyCell cost={r.flagged.total.cost_usd} sub={commitsSub(r.flagged.total.count)} />
          </td>
          <td>
            <MoneyCell cost={r.grandTotal.cost_usd} sub={commitsSub(r.grandTotal.count)} />
          </td>
        </tr>
      </tfoot>
    </table>
  );
}
